import { useNavigate } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useGroups } from '../api/hooks'
import { BackButton, GameThumb, TopBar } from '../primitives'
import { useRecordPlayDraft } from '../stores/recordPlayDraft'

/**
 * Scope picker — the first step of recording a play, reached from the
 * nav's ➕. Choosing a scope clears any stale draft and drops the user
 * into the matching wizard: a group flow, or the no-group flow.
 *
 * Rendered as the `/record` index (see router); the no-group record
 * layout deliberately skips its wizard chrome for this screen.
 */
export const RecordScopeScreen = () => {
  const navigate = useNavigate()
  const groups = useGroups()
  const reset = useRecordPlayDraft((s) => s.reset)

  const pickNoGroup = () => {
    reset(null)
    navigate({ to: '/record/game' })
  }
  const pickGroup = (groupId: string) => {
    reset(groupId)
    navigate({ to: '/group/$id/record/game', params: { id: groupId } })
  }

  return (
    <div className="paper" style={{ minHeight: '100vh' }}>
      <TopBar
        left={<BackButton fallback={() => navigate({ to: '/home' })} />}
        title="Record a play"
        sub="Where should it count?"
      />

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ScopeCard
          title="Just friends"
          subtitle="not tied to a group"
          thumb={<GameThumb label="JF" size={44} palette="slate" />}
          onClick={pickNoGroup}
        />

        {groups.isLoading && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '4px 2px' }}>Loading…</div>
        )}

        {groups.data?.map((g) => (
          <ScopeCard
            key={g.id}
            title={g.name}
            subtitle={g.description ?? undefined}
            thumb={<GameThumb label={g.name} size={44} />}
            onClick={() => pickGroup(g.id)}
          />
        ))}
      </div>
    </div>
  )
}

const ScopeCard = ({
  title,
  subtitle,
  thumb,
  onClick,
}: {
  title: string
  subtitle?: string
  thumb: ReactNode
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    className="felt-card"
    style={{
      padding: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      width: '100%',
      textAlign: 'left',
      font: 'inherit',
      color: 'inherit',
      cursor: 'pointer',
    }}
  >
    {thumb}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: 15 }}>{title}</div>
      {subtitle && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--ink-3)',
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
    <span
      style={{ color: 'var(--terra)', fontSize: 22, fontWeight: 700, lineHeight: 1 }}
      aria-hidden
    >
      →
    </span>
  </button>
)
